import React, { memo, useMemo, useState, useEffect } from 'react'
import styled from 'styled-components'
import { FlexDivRow } from 'components/layout/flex'
import { useAppSelector } from 'state/hooks'
import { selectCurrentMarketInfo, selectOrderDirection, selectOrderPrice, selectOrderType, selectOrderSize, selectOrderTotal } from 'state/exchange/selectors'
import { selectBalanceInBank } from 'state/wallet/selectors'
import { selectOraclePrices } from 'state/prices/selectors'
import { OrderDirection } from '@bitly/sdk/types'
import { InfoBoxRow } from 'components/InfoBox'
import { formatCurrency, formatDollars } from 'utils/prices'
import { LIMIT_ORDER_TRANSACTION_FEE_PERCENT, MARKET_ORDER_TRANSACTION_FEE_PERCENT } from 'constants/defaults'

type MarginInputProps = {
	isMobile?: boolean
}

const InfoBlock: React.FC<MarginInputProps> = memo(({ isMobile }) => {
	const marketInfo = useAppSelector(selectCurrentMarketInfo)
	const balances = useAppSelector(selectBalanceInBank)
	const orderDirection = useAppSelector(selectOrderDirection)
	const orderPrice = Number(useAppSelector(selectOrderPrice))
	const orderType = useAppSelector(selectOrderType)
	const orderSize = useAppSelector(selectOrderSize)
	const orderTotal = useAppSelector(selectOrderTotal)
	const oraclePrices = useAppSelector(selectOraclePrices)
	const [tokenYPrice, setTokenYPrice] = useState<number>(0)

	// Get tokenY price from Redux state when component mounts or when oraclePrices or marketInfo changes
	useEffect(() => {
		if (marketInfo?.tokenY.symbol && oraclePrices) {
			const price = oraclePrices[marketInfo.tokenY.symbol]
			setTokenYPrice(price ?? 0)
		}
	}, [marketInfo, oraclePrices])

	const originTokenbalances = useMemo(() => {
		if (orderDirection == OrderDirection.buy) {
			return balances[marketInfo?.tokenY.address ?? ''] ?? 0
		} else {
			return balances[marketInfo?.tokenX.address ?? ''] ?? 0
		}
	}, [balances, marketInfo, orderDirection])

	const originTokenSymbol = useMemo(() => {
		if (orderDirection == OrderDirection.buy) {
			return marketInfo?.tokenY.symbol ?? ''
		} else {
			return marketInfo?.tokenX.symbol ?? ''
		}
	}, [marketInfo, orderDirection])

	const targetTokenSymbol = useMemo(() => {
		if (orderDirection == OrderDirection.buy) {
			return marketInfo?.tokenX.symbol ?? ''
		} else {
			return marketInfo?.tokenY.symbol ?? ''
		}
	}, [marketInfo, orderDirection])

	const maxSize = useMemo(() => {
		if (orderDirection == OrderDirection.sell) {
			return originTokenbalances * orderPrice
		} else {
			return orderPrice === 0 
				? 0 
				: originTokenbalances / orderPrice
		}
	}, [orderPrice, originTokenbalances])

	const volume = useMemo(() => {
		if (orderDirection === OrderDirection.buy) {
			return Number(orderSize) || 0
		} else {
			return Number(orderTotal) || 0
		}
	}, [orderDirection, orderSize, orderTotal])

	const transactionFee = useMemo(() => {
		const fee = orderType === 'limit' ? LIMIT_ORDER_TRANSACTION_FEE_PERCENT : MARKET_ORDER_TRANSACTION_FEE_PERCENT
		return fee * 0.01 * volume
	}, [volume])

	const youWillGet = useMemo(() => {
		if (orderDirection === OrderDirection.buy) {
			return Number(orderSize) - transactionFee
		} else {
			return Number(orderTotal) - transactionFee
		}
	}, [orderDirection, orderSize, orderTotal, transactionFee])

	return (
		<>
			<Container>
				<InfoBoxRow
					title="Available"
					textValue={formatCurrency(marketInfo?.marketAddress ?? '', originTokenbalances, {
						currencyKey: originTokenSymbol
					}) + (originTokenSymbol === marketInfo?.tokenY.symbol && tokenYPrice > 0 ?
						` (${formatDollars(originTokenbalances * tokenYPrice)})` : '')}
				/>
				
				{orderType !== 'market' && <InfoBoxRow
					title={`Max ${orderDirection === OrderDirection.buy ? 'Buy' : 'Sell'}`}
					textValue={formatCurrency(marketInfo?.marketAddress ?? '', maxSize, {
						currencyKey: targetTokenSymbol
					}) + (targetTokenSymbol === marketInfo?.tokenY.symbol && tokenYPrice > 0 ?
						` (${formatDollars(maxSize * tokenYPrice)})` : '')}
				/>}

				<InfoBoxRow
					title="Txn Fee"
					textValue={formatCurrency(marketInfo?.marketAddress ?? '', transactionFee, {
						currencyKey: targetTokenSymbol
					}) + (targetTokenSymbol === marketInfo?.tokenY.symbol && tokenYPrice > 0 ?
						` (${formatDollars(transactionFee * tokenYPrice)})` : '')}
				/>

				<InfoBoxRow
					title="You Will Get"
					textValue={formatCurrency(marketInfo?.marketAddress ?? '', youWillGet, {
						currencyKey: targetTokenSymbol
					}) + (targetTokenSymbol === marketInfo?.tokenY.symbol && tokenYPrice > 0 ?
						` (${formatDollars(youWillGet * tokenYPrice)})` : '')}
				/>
			</Container>
		</>
	)
})

const Container = styled.div`
	// margin-top: 18px;
	// margin-bottom: 16px;
`

export default InfoBlock
